import React from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { Formik, Field, ErrorMessage } from 'formik';
import Select from 'react-select';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import * as api from '~/Services/Service';

const OPCOES_CATEGORIA = [
  { value: 'D', label: 'Despesa' },
  { value: 'P', label: 'Projeto' },
];

const OPCOES_PROJETO = [
  { value: 'I', label: 'Item' },
  { value: 'S', label: 'Sub Item' },
];

const OPCOES_STATUS = [
  { value: 'A', label: 'Ativo' },
  { value: 'I', label: 'Inativo' },
];

const buildSchema = (parametros, ehEdicao) => {
  if (ehEdicao) {
    return Yup.object().shape({
      descricao: Yup.string()
        .required('Descrição é obrigatória')
        .max(60, 'Máximo 60 caracteres'),
      status: Yup.string()
        .required('Status é obrigatório')
        .oneOf(['A', 'I']),
    });
  }
  return Yup.object().shape({
    categoria: Yup.string()
      .required('Categoria é obrigatória')
      .oneOf(['D', 'P'], 'Categoria inválida'),
    codigo: Yup.mixed().when('categoria', {
      is: 'D',
      then: Yup.number()
        .typeError('Código deve ser numérico')
        .required('Código é obrigatório')
        .positive('Código deve ser maior que zero')
        .integer('Código deve ser inteiro')
        .test(
          'fora-faixa-projeto',
          'Código inválido! Faixa reservada para Pagamento Projeto!',
          (value) => {
            if (value == null) return true;
            const ini = parametros ? parametros.cc_ini_projeto : null;
            const fim = parametros ? parametros.cc_fim_projeto : null;
            if (ini == null || fim == null) return true;
            return !(value >= ini && value <= fim);
          }
        ),
      otherwise: Yup.mixed().nullable(),
    }),
    descricao: Yup.string()
      .required('Descrição é obrigatória')
      .max(60, 'Máximo 60 caracteres'),
    projeto: Yup.string().when('categoria', {
      is: 'P',
      then: Yup.string().required('Projeto é obrigatório').oneOf(['I', 'S']),
      otherwise: Yup.string().nullable(),
    }),
    status: Yup.string()
      .required('Status é obrigatório')
      .oneOf(['A', 'I']),
  });
};

const ModalCentroCusto = (props) => {
  const show = props.show;
  const onHide = props.onHide;
  const item = props.item;
  const parametros = props.parametros;
  const onSalvar = props.onSalvar;

  const ehEdicao = item != null;

  const getInitialValues = () => {
    if (item) {
      return {
        codigo: item.codigo,
        descricao: item.descricao || '',
        categoria: item.categoria || 'D',
        projeto: item.projeto || 'I',
        status: item.status || 'A',
      };
    }
    return {
      codigo: '',
      descricao: '',
      categoria: 'D',
      projeto: 'I',
      status: 'A',
    };
  };

  const handleSubmit = async (values, formikBag) => {
    try {
      if (ehEdicao) {
        // Projeto NÃO entra no body — é read-only em edição (paridade Delphi UCentro.pas L175).
        // Tipo tampouco é enviado — backend reenvia o valor atual via ObterPorCodigo (RN-11).
        // Código vem direto do item (não do formulário) — o campo é escondido em edição.
        await api.atualizarCentroCusto({
          codigo: item.codigo,
          descricao: values.descricao,
          status: values.status,
        });
        toast.success('Centro de custo atualizado com sucesso.');
      } else if (values.categoria === 'P') {
        await api.inserirCentroCusto({
          categoria: 'P',
          descricao: values.descricao,
          projeto: values.projeto,
          status: values.status,
        });
        toast.success('Centro de custo criado com sucesso.');
      } else {
        await api.inserirCentroCusto({
          categoria: 'D',
          codigo: Number(values.codigo),
          descricao: values.descricao,
          status: values.status,
        });
        toast.success('Centro de custo criado com sucesso.');
      }
      onSalvar();
      onHide();
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro ao salvar centro de custo.');
    } finally {
      formikBag.setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton style={{ backgroundColor: '#0747a6', color: '#fff' }}>
        <Modal.Title style={{ fontSize: '1rem', color: '#fff' }}>
          {ehEdicao ? 'Editar Centro de Custo' : 'Novo Centro de Custo'}
        </Modal.Title>
      </Modal.Header>

      <Formik
        initialValues={getInitialValues()}
        validationSchema={buildSchema(parametros, ehEdicao)}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formik) => (
          <Form onSubmit={formik.handleSubmit}>
            <Modal.Body>

              {/* Categoria — oculta em edição (imutável, RN-08/RN-09) */}
              {!ehEdicao && (
                <Form.Group className="mb-3">
                  <Form.Label className="font-weight-bold mb-1">
                    Categoria <span className="text-danger">*</span>
                  </Form.Label>
                  <Select
                    options={OPCOES_CATEGORIA}
                    value={OPCOES_CATEGORIA.find((o) => o.value === formik.values.categoria) || null}
                    onChange={(opt) => {
                      formik.setFieldValue('categoria', opt ? opt.value : '');
                      formik.setFieldTouched('categoria', true);
                    }}
                    placeholder="Selecione..."
                  />
                  <ErrorMessage
                    name="categoria"
                    component="div"
                    className="invalid-feedback d-block"
                  />
                </Form.Group>
              )}

              {/* Código — oculto em edição (imutável, RN-08/RN-09) */}
              {!ehEdicao && (
                <Form.Group className="mb-3">
                  <Form.Label className="font-weight-bold mb-1">
                    Código {formik.values.categoria !== 'P' && <span className="text-danger">*</span>}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="codigo"
                    value={formik.values.codigo}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    readOnly={formik.values.categoria === 'P'}
                    disabled={formik.values.categoria === 'P'}
                    placeholder={formik.values.categoria === 'P' ? 'Gerado automaticamente' : ''}
                    style={formik.values.categoria === 'P' ? { backgroundColor: '#f5f5f5' } : {}}
                    isInvalid={formik.touched.codigo && !!formik.errors.codigo}
                  />
                  <ErrorMessage
                    name="codigo"
                    component="div"
                    className="invalid-feedback d-block"
                  />
                </Form.Group>
              )}

              {/* Descrição */}
              <Form.Group className="mb-3">
                <Form.Label className="font-weight-bold mb-1">
                  Descrição <span className="text-danger">*</span>
                </Form.Label>
                <Field
                  as={Form.Control}
                  type="text"
                  name="descricao"
                  maxLength={60}
                  style={{ textTransform: 'uppercase' }}
                  isInvalid={formik.touched.descricao && !!formik.errors.descricao}
                />
                <ErrorMessage
                  name="descricao"
                  component="div"
                  className="invalid-feedback d-block"
                />
              </Form.Group>

              {/* Projeto — visível apenas quando Categoria=P e não em edição (imutável, RN-06) */}
              {!ehEdicao && formik.values.categoria === 'P' && (
                <Form.Group className="mb-3">
                  <Form.Label className="font-weight-bold mb-1">
                    Projeto <span className="text-danger">*</span>
                  </Form.Label>
                  <Select
                    options={OPCOES_PROJETO}
                    value={OPCOES_PROJETO.find((o) => o.value === formik.values.projeto) || null}
                    onChange={(opt) => {
                      formik.setFieldValue('projeto', opt ? opt.value : '');
                      formik.setFieldTouched('projeto', true);
                    }}
                    isDisabled={ehEdicao}
                  />
                  <ErrorMessage
                    name="projeto"
                    component="div"
                    className="invalid-feedback d-block"
                  />
                </Form.Group>
              )}

              {/* Status */}
              <Form.Group className="mb-3">
                <Form.Label className="font-weight-bold mb-1">
                  Status <span className="text-danger">*</span>
                </Form.Label>
                <Select
                  options={OPCOES_STATUS}
                  value={OPCOES_STATUS.find((o) => o.value === formik.values.status) || null}
                  onChange={(opt) => {
                    formik.setFieldValue('status', opt ? opt.value : '');
                    formik.setFieldTouched('status', true);
                  }}
                />
                <ErrorMessage
                  name="status"
                  component="div"
                  className="invalid-feedback d-block"
                />
              </Form.Group>

            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={onHide} disabled={formik.isSubmitting}>
                Cancelar
              </Button>
              <Button variant="primary" type="submit" disabled={formik.isSubmitting}>
                {formik.isSubmitting ? 'Salvando...' : 'Salvar'}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default ModalCentroCusto;
